import { FC, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useMutation, useQuery } from 'react-query';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { Button } from 'components/Core/Button';
import { Switch } from 'components/Core/Switch/Switch';
import { ImageUploader } from 'components/Core/ImageUploader/ImageUploader';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import SelectComponent from 'components/Inputs/SelectComponent';
import { FallbackCategoryRequiredError } from 'stores/categories/errors';
import { IPointsCategoryDataWithTranslations } from 'stores/categories/types';
import { StatusType } from 'types/common';
import { pointCategoryBasicFormTranslations } from './translations';
import { PointCategoryBasicFormTransFields, IPointCategoryBasicForm, IPointCategoryStatusForm } from './types';
import styles from './pointCategoryBasicForm.module.scss';

const IMAGE_ACCEPT = ['image/png', 'image/webp'];
const IMAGE_MAX_MB = 2;

// Only what the form edits; status, counts and translations stay in the store.
const toFormValues = ({
  categoryName,
  categoryImage,
  categoryImageDark,
}: IPointsCategoryDataWithTranslations): IPointCategoryBasicForm => ({
  categoryName,
  categoryImage,
  categoryImageDark,
});

const PointCategoryBasicForm: FC = () => {
  const {
    categoriesStore: {
      pointsCategoryData,
      pointsCategories,
      getCategoriesData,
      patchPointsCategory,
      changeCategoryStatus,
    },
    alertStore: { showAlert },
    popUpStore: { openPopUp, closePopUp },
  } = useStore();

  const { t } = useMyTranslations<PointCategoryBasicFormTransFields>([pointCategoryBasicFormTranslations.name]);

  // ── Status card: saves on its own, Save below never touches it ──────────

  // Points that would be left with no category at all — the switch cannot be
  // turned off until one of the remaining Active categories takes them over.
  const orphanPointsCount = pointsCategoryData?.orphanPointsCount || 0;

  // Fallback options come from the same list the categories table shows; only
  // needed when there is something to hand over.
  useQuery('getPointsCategoriesList', () => getCategoriesData('points-categories'), {
    retry: false,
    refetchOnWindowFocus: false,
    enabled: orphanPointsCount > 0,
  });

  const statusFormMethods = useForm<IPointCategoryStatusForm>({
    defaultValues: { status: pointsCategoryData?.status },
  });
  const {
    watch: watchStatus,
    setValue: setStatusValue,
    reset: resetStatus,
    handleSubmit: handleStatusSubmit,
  } = statusFormMethods;

  const status = watchStatus('status');
  const fallbackCategory = watchStatus('fallbackCategory');
  const isDeactivating = status === 'Inactive' && pointsCategoryData?.status === 'Active';
  const needsFallback = isDeactivating && orphanPointsCount > 0;

  const fallbackOptions = useMemo(
    () => (pointsCategories || []).filter((item) => item.id !== pointsCategoryData?.id && item.status === 'Active'),
    [pointsCategories, pointsCategoryData?.id]
  );

  const { mutate: mutateStatus, isLoading: isStatusLoading } = useMutation(changeCategoryStatus, {
    mutationKey: 'changeCategoryStatus',
    onSuccess: () => showAlert(t('statusSaved'), 'success'),
    onError: (error) => {
      // The count on the page went stale and the backend still sees orphans.
      if (error instanceof FallbackCategoryRequiredError) {
        showAlert(t('orphanWarning', { points: error.orphanPointsCount }), 'warning');
      }
      // Nothing changed server-side, so the store value is still the truth.
      resetStatus({ status: pointsCategoryData?.status });
    },
  });

  // Success lands in the store first (changeCategoryStatus writes the response
  // into pointsCategoryData); the card follows it and drops any armed fallback.
  useEffect(() => {
    resetStatus({ status: pointsCategoryData?.status });
  }, [pointsCategoryData?.status, resetStatus]);

  // Main form state and its request live here, above the handlers, because
  // the status card and the form share one busy flag.
  const useFormMethods = useForm<IPointCategoryBasicForm>({
    defaultValues: pointsCategoryData ? toFormValues(pointsCategoryData) : undefined,
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useFormMethods;

  const { mutate, isLoading } = useMutation(patchPointsCategory, {
    mutationKey: 'patchPointsCategory',
    onSuccess: (saved) => {
      showAlert(t('saved'), 'success');
      // New defaults: the saved URLs replace the blob previews, the *File
      // fields drop out and the form is clean again.
      if (saved) reset(toFormValues(saved));
    },
  });

  // Either request in flight freezes the whole page: the status card and the
  // form share one entity, so neither may be edited while the other saves.
  const isBusy = isLoading || isStatusLoading;

  // The field errors mark the inputs; the alert says outright why nothing was sent.
  const onInvalid = useCallback(() => showAlert(t('validationFailed'), 'warning'), [showAlert, t]);

  const onToggleStatus = useCallback(() => {
    if (!pointsCategoryData || isBusy) return;
    const next: StatusType = status === 'Active' ? 'Inactive' : 'Active';
    const willNeedFallback = next === 'Inactive' && pointsCategoryData.status === 'Active' && orphanPointsCount > 0;
    // The switch moves at once in every case. A deactivation that strands
    // points only arms the fallback picker, flipping back cancels it without
    // a request; a plain flip saves right away and is rolled back by onError.
    setStatusValue('status', next);
    if (willNeedFallback || next === pointsCategoryData.status) return;
    mutateStatus({ id: pointsCategoryData.id, status: next });
  }, [pointsCategoryData, status, orphanPointsCount, setStatusValue, mutateStatus, isBusy]);

  const onDeactivateWithFallback = useCallback(
    (data: IPointCategoryStatusForm) => {
      if (!pointsCategoryData) return;
      mutateStatus({ id: pointsCategoryData.id, status: 'Inactive', fallbackCategoryId: data.fallbackCategory?.id });
    },
    [pointsCategoryData, mutateStatus]
  );

  // ── Main form: name + images, one multipart PATCH ───────────────────────

  const onSubmitHandler = useCallback(
    (data: IPointCategoryBasicForm) => {
      mutate({
        categoryName: data.categoryName,
        // A freshly picked file, or the URL that came from the backend.
        categoryImage: data.categoryImageFile || data.categoryImage,
        categoryImageDark: data.categoryImageDarkFile || data.categoryImageDark,
      });
    },
    [mutate]
  );

  // Save first passes validation, then asks: the change lands in every country.
  const onSaveClick = useCallback(
    () =>
      !isBusy &&
      handleSubmit(
        (data) =>
          openPopUp({
            formName: 'confirmation',
            title: t('confirmTitle'),
            description: t('confirmText'),
            buttonLabel: t('confirm'),
            cb: () => {
              closePopUp();
              onSubmitHandler(data);
            },
          }),
        onInvalid
      )(),
    [handleSubmit, openPopUp, closePopUp, onSubmitHandler, onInvalid, t, isBusy]
  );

  return (
    <div className={styles.content}>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <FormProvider {...(statusFormMethods as any)}>
        <section className={styles.statusCard}>
          <div className={styles.checkboxWrap}>
            <h6 className={styles.title}>{t('status')}</h6>
            <Switch isActive={status === 'Active'} onChange={onToggleStatus} disabled={isBusy} />
          </div>
          {/* Says outright why no fallback select is offered: nothing to hand over. */}
          <p className={styles.hint}>
            {t('pointsCount', { points: pointsCategoryData?.pointsCount || 0, orphans: orphanPointsCount })}
          </p>
          {needsFallback && (
            <>
              <p className={styles.warning}>{t('orphanWarning', { points: orphanPointsCount })}</p>
              <SelectComponent
                name='fallbackCategory'
                label={t('fallbackCategory')}
                options={fallbackOptions}
                optionLabel='categoryName'
                optionValue='id'
                maxMenuHeight={160}
                required
                isDisabled={isBusy}
              />
              <Button
                title={t('deactivate')}
                onClick={handleStatusSubmit(onDeactivateWithFallback, onInvalid)}
                isDisabled={isBusy || !fallbackCategory}
              />
            </>
          )}
        </section>
      </FormProvider>

      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <FormProvider {...(useFormMethods as any)}>
        <form className={styles.form}>
          <section className={`${styles.leftSection}`}>
            <div className={`${styles.formSection}`}>
              <h6 className={`${styles.title} ${styles.titleWithMargin}`}>{t('title')}</h6>
              <ControlledInput name='categoryName' type='text' required label={t('categoryName')} disabled={isBusy} />
            </div>
            <div className={styles.bottomSection}>
              <Button title={t('save')} onClick={onSaveClick} isDisabled={isBusy || !isDirty} />
              {/* The backend applies the change to every country (MOB1-2123). */}
              <p className={styles.saveHint}>{t('allCountriesHint')}</p>
            </div>
          </section>

          <section className={styles.rightSection}>
            <ImageUploader
              name='categoryImage'
              title={t('categoryImage')}
              required
              width={288}
              height={300}
              classNameProp={styles.uploader}
              accept={IMAGE_ACCEPT}
              maxSizeMb={IMAGE_MAX_MB}
              disabled={isBusy}
            />
            <ImageUploader
              name='categoryImageDark'
              title={t('categoryImageDark')}
              required
              width={288}
              height={300}
              classNameProp={`${styles.uploader} ${styles.uploaderDark}`}
              accept={IMAGE_ACCEPT}
              maxSizeMb={IMAGE_MAX_MB}
              disabled={isBusy}
            />
          </section>
        </form>
      </FormProvider>
    </div>
  );
};

export default observer(PointCategoryBasicForm);
